'use client';

import { tracking } from '@/lib/features/tracking';
import { Button, Card, CardBody, Spinner } from '@heroui/react';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { memo, useEffect, useState } from 'react';
import { TopLevelSpec } from 'vega-lite';
import { ChatChartComponentProps } from '../props';

// Global state for VegaEmbed component
let VegaEmbedComponent: React.ComponentType<{
    spec: TopLevelSpec;
    onError?: (error: unknown) => void;
}> | null = null;
let isVegaLoaded = false;
let vegaLoadPromise: Promise<unknown> | null = null;

function ChatChartComponentImpl(props: ChatChartComponentProps) {
    const { chartSpec } = props;
    const [isLoaded, setIsLoaded] = useState(isVegaLoaded);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // If already loaded, set state immediately
        if (isVegaLoaded && VegaEmbedComponent) {
            setIsLoaded(true);
            return;
        }

        // If already loading, wait for the existing promise
        if (vegaLoadPromise) {
            vegaLoadPromise
                .then(() => {
                    setIsLoaded(true);
                })
                .catch((_error) => {
                    tracking.errorWithSource(_error as Error, 'ChatChartComponent.tsx', {
                        chartSpec: chartSpec,
                    });
                    setError('Failed to load chart component');
                });
            return;
        }

        // Start loading vega
        vegaLoadPromise = import('react-vega')
            .then((mod) => {
                VegaEmbedComponent = mod.VegaEmbed;
                isVegaLoaded = true;
                setIsLoaded(true);
                return mod;
            })
            .catch((_error) => {
                tracking.errorWithSource(_error as Error, 'ChatChartComponent.tsx', {
                    chartSpec: chartSpec,
                });
                setError('Failed to load chart component');
                throw _error;
            });
    }, []);

    // Validate chart spec
    if (!chartSpec || typeof chartSpec !== 'object') {
        return null;
    }

    // Check if chart spec has valid data
    if (!chartSpec.data || !(chartSpec as { mark: unknown }).mark) {
        return null;
    }

    // Check if data is valid
    if (chartSpec.data && typeof chartSpec.data === 'object' && 'values' in chartSpec.data) {
        const values = (chartSpec.data as { values: unknown }).values;
        if (!Array.isArray(values) || values.length === 0) {
            return null;
        }
    }

    if (error) {
        return (
            <motion.div
                className="group/message mx-auto w-full max-w-full pl-0 pr-4"
                initial={{ y: 5, opacity: 0 }}
                animate={{ y: 0, opacity: 1, transition: { delay: 0 } }}
            >
                <Card className="border-danger-200 bg-danger-50">
                    <CardBody className="flex flex-col items-center p-6 text-center">
                        <Icon
                            icon="lucide:alert-triangle"
                            className="mb-3 text-danger-500"
                            width={32}
                        />
                        <p className="text-mini font-medium text-danger-800">
                            Chart Loading Unsuccessful
                        </p>
                        <Button
                            size="sm"
                            color="danger"
                            variant="flat"
                            onPress={() => window.location.reload()}
                            startContent={<Icon icon="lucide:refresh-cw" width={14} />}
                        >
                            Refresh Page
                        </Button>
                    </CardBody>
                </Card>
            </motion.div>
        );
    }

    if (!isLoaded || !VegaEmbedComponent) {
        return (
            <motion.div
                className="group/message mx-auto w-full max-w-full pl-0 pr-4"
                initial={{ y: 5, opacity: 0 }}
                animate={{ y: 0, opacity: 1, transition: { delay: 0 } }}
            >
                <Card className="border-primary-200 bg-primary-50">
                    <CardBody className="flex flex-col items-center p-6 text-center">
                        <Spinner size="lg" color="primary" className="mb-3" />
                        <p className="text-mini text-primary-600">Loading visualization...</p>
                    </CardBody>
                </Card>
            </motion.div>
        );
    }

    return (
        <motion.div
            className="group/message mx-auto w-full max-w-full pl-0 pr-4 text-center"
            initial={{ y: 5, opacity: 0 }}
            animate={{ y: 0, opacity: 1, transition: { delay: 0 } }}
        >
            <div className="relative z-10 w-full overflow-x-auto">
                <VegaEmbedComponent
                    spec={chartSpec}
                    onError={(_error: unknown) => {
                        setError('Chart rendering error');
                    }}
                />
            </div>
        </motion.div>
    );
}

export const ChatChartComponent = memo(ChatChartComponentImpl, (prevProps, nextProps) => {
    // Deep equality check for chartSpec to prevent unnecessary re-renders
    return JSON.stringify(prevProps.chartSpec) === JSON.stringify(nextProps.chartSpec);
});
